import React, { useCallback, useMemo, useRef, useState } from 'react';

import { Button, ButtonGroup } from '@mui/material';
import { ArrowCheckIcon } from '@sharedCR/coreDesignSystem/icons';
import MenuPopoverCDS from '@sharedCR/coreDesignSystem/MenuPopoverCDS';
import PropTypes from 'prop-types';

import { IconSize } from 'constants/coreDesignSystem';
import usePopover from 'hooks/usePopover';

import { FormsTestIds } from '../../Forms.testIds';
import BatchAddDynamicFields from '../BatchAddDynamicFields';

import { AddDynamicFieldLabel } from './AddDynamicFieldButton.constants';

const AddDynamicFieldButton = ({ selectedPage, onAddDynamicField, isDisabled = false }) => {
  const buttonGroupRef = useRef(null);
  const [batchAnchorEl, setBatchAnchorEl] = useState(null);
  const {
    anchorEl: menuAnchorEl,
    onSetAnchorElement: onOpenMenu,
    onClearAnchorElement: onCloseMenu,
  } = usePopover();

  const onOpenBatch = useCallback(() => setBatchAnchorEl(buttonGroupRef.current), []);
  const onCloseBatch = useCallback(() => setBatchAnchorEl(null), []);

  const menuItems = useMemo(
    () => [
      {
        text: AddDynamicFieldLabel.SINGLE,
        onClick: onAddDynamicField,
        'data-testid': FormsTestIds.PDF_EDITOR_ADD_DYNAMIC_FIELD_MENU_ITEM,
      },
      {
        text: AddDynamicFieldLabel.BATCH,
        onClick: onOpenBatch,
        'data-testid': FormsTestIds.PDF_EDITOR_BATCH_ADD_DYNAMIC_FIELDS_MENU_ITEM,
      },
    ],
    [onAddDynamicField, onOpenBatch],
  );

  return (
    <>
      <ButtonGroup
        className="mr-5"
        color="primary"
        disabled={isDisabled}
        ref={buttonGroupRef}
        size="small"
        variant="contained"
      >
        <Button
          data-testid={FormsTestIds.PDF_EDITOR_ADD_DYNAMIC_FIELD_BUTTON}
          onClick={onAddDynamicField}
        >
          {AddDynamicFieldLabel.SINGLE}
        </Button>

        <Button
          aria-haspopup="menu"
          aria-label={AddDynamicFieldLabel.MENU_BUTTON_ARIA}
          data-testid={FormsTestIds.PDF_EDITOR_ADD_DYNAMIC_FIELD_MENU_BUTTON}
          onClick={onOpenMenu}
        >
          <ArrowCheckIcon size={IconSize[16]} />
        </Button>
      </ButtonGroup>

      <MenuPopoverCDS anchorEl={menuAnchorEl} items={menuItems} onClose={onCloseMenu} />

      <BatchAddDynamicFields
        anchorEl={batchAnchorEl}
        selectedPage={selectedPage}
        onClose={onCloseBatch}
      />
    </>
  );
};

AddDynamicFieldButton.propTypes = {
  selectedPage: PropTypes.number.isRequired,
  onAddDynamicField: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
};

export default AddDynamicFieldButton;
